import React, { Fragment, useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import Select from "react-select";

import { EndPoints } from "../../Helpers/Constants/EndPoints";
import Loader from "../Common/Loader";
import API from "../../Helpers/Constants/EndPoints/Api";
import Axios from "../../Helpers/Universal/Axios/Axios";
import {
  BLOCK_STATUS,
  LIMIT,
  NA,
  OFFSET,
  USER_TYPE,
} from "../../Helpers/Constants/Default";
import Paginate from "../Common/Paginate";
import DeleteConfirmation from "../Common/DeleteConfirmation ";
import { useSelector } from "react-redux";
import DataNotFound from "../Common/DataNotFound";
import { City, State } from "country-state-city";

const Agents = ({ permissions: { all, view, add, edit, delete: del } }) => {
  const paginate = useSelector((val) => val.paginate);
  const [data, setData] = useState([]);
  const [visible, setVisible] = useState({
    loader: false,
    deleteConformation: { data: false, visible: false },
  });
  
  // Separate states for each filter
  const [managerFilter, setManagerFilter] = useState([]);
  const [supervisorFilter, setSupervisorFilter] = useState([]);
  const [searchFilter, setSearchFilter] = useState("");
  const [stateFilter, setStateFilter] = useState(null);
  const [cityFilter, setCityFilter] = useState(null);
  
  const [dropdown, setDropdown] = useState({
    supervisors: [],
    managers: [],
    supervisorOptions: [],
    managerOptions: [],
  });
  
  const [filter, setFilter] = useState({
    limit: LIMIT,
    offset: paginate?.agent || OFFSET,
  });

  const dropdownRefs = useRef({});

  const getData = async () => {
    try {
      setVisible((prev) => ({ ...prev, loader: true }));
      
      // Build query parameters from individual filter states
      const params = {
        limit: filter.limit,
        offset: filter.offset
      };
      
      // Add filters only if they have values
      if (managerFilter.length > 0) {
        params.managerId = managerFilter.map(m => m.value);
      }
      
      if (supervisorFilter.length > 0) {
        params.supervisorId = supervisorFilter.map(s => s.value);
      }
      
      if (searchFilter) {
        params.search = searchFilter;
      }
      
      if (stateFilter) {
        params.states = [stateFilter];
      }
      
      if (cityFilter) {
        params.cities = [cityFilter];
      }
      
      const { status, data } = await Axios.get(API.AGENT.LISTING, params);
      if (status) setData(data);
    } catch (err) {
      console.log("ERROR==>", err);
    } finally {
      setVisible((prev) => ({ ...prev, loader: false }));
    }
  };

  const handleBulkDelete = async () => {
    try {
      Swal.fire({
        title: `Delete All Data`,
        text: `Are you sure you want to Delete all Agents Data?`,
        confirmButtonText: "Yes",
        showCancelButton: true,
      })
        .then(async (result) => {
          if (result.isConfirmed) {
            setVisible((prev) => ({ ...prev, loader: true }));
            await Axios.delete(API.AGENT.BULK_DELETE);
          }
        })
        .finally(() => {
          setVisible((prev) => ({ ...prev, loader: false }));
        });
    } catch (err) {
      console.log("ERROR==>", err);
    } finally {
      setVisible((prev) => ({ ...prev, loader: false }));
    }
  };

  const handleBlockUnblock = async ({ _id, isBlocked }) => {
    try {
      Swal.fire({
        title: `${isBlocked ? "Block" : "Un-block"} Agent`,
        text: `Are you sure you want to ${isBlocked ? "Block" : "Un-block"}?`,
        confirmButtonText: "Yes",
        showCancelButton: true,
      }).then(async (result) => {
        if (result.isConfirmed) {
          const { status } = await Axios.patch(API.AGENT.UPDATE, {
            agentId: _id,
            isBlocked: !isBlocked,
          });
          if (status)
            setData((prev) => ({
              ...prev,
              data: prev?.data?.map((val) =>
                val._id === _id ? { ...val, isBlocked: !isBlocked } : val
              ),
            }));
        }
      });
    } catch (err) {
      console.log("ERROR==>", err);
    }
  };

  const handleResetPassword = async (agentId) => {
    try {
      setVisible((prev) => ({ ...prev, loader: true }));
      await Axios.get(API.AGENT.RESET_PASSWORD + "/" + agentId);
    } catch (err) {
      console.log("ERROR==>", err);
    } finally {
      setVisible((prev) => ({ ...prev, loader: false }));
    }
  };

  const handleDelete = async (data) => {
    try {
      setVisible((prev) => ({
        ...prev,
        deleteConformation: { visible: true, data },
      }));
    } catch (err) {
      console.log("ERROR==>", err);
    }
  };

  const handleAction = (agentId, e) => {
    e.stopPropagation();
    setData((prev) => ({
      ...prev,
      data: prev?.data?.map((val) =>
        val._id === agentId && !val?.action
          ? { ...val, action: true }
          : { ...val, action: false }
      ),
    }));
  };

  const handleClickOutside = (e) => {
    const isOutside = Object.values(dropdownRefs.current).every((ref) => {
      return ref && !ref.contains(e.target);
    });

    if (isOutside) {
      setData((prev) => ({
        ...prev,
        data: prev?.data?.map((val) => ({
          ...val,
          action: false,
        })),
      }));
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getDropdowns = async () => {
    try {
      const [supervisor, manager] = await Promise.all([
        Axios.get(API.DROPDOWN.USER, {
          accountType: USER_TYPE.SUPERVISOR,
        }),
        Axios.get(API.DROPDOWN.USER, {
          accountType: USER_TYPE.MANAGER,
        }),
      ]);

      if (supervisor.status) {
        const supervisorOptions = supervisor.data.map((user) => ({
          value: user._id,
          label: user.name,
        }));
        setDropdown((prev) => ({
          ...prev,
          supervisors: supervisor.data,
          supervisorOptions,
        }));
      }

      if (manager.status) {
        const managerOptions = manager.data.map((user) => ({
          value: user._id,
          label: user.name,
        }));
        setDropdown((prev) => ({
          ...prev,
          managers: manager.data,
          managerOptions,
        }));
      }
    } catch (err) {
      console.log("ERROR==>", err);
    }
  };

  const loadSupervisorData = async (managerIds) => {
    try {
      if (!managerIds || managerIds.length === 0) {
        setDropdown((prev) => ({
          ...prev,
          supervisors: [],
          supervisorOptions: [],
        }));
        setSupervisorFilter([]);
        return;
      }

      const res = await Axios.post(API.NOTIFICATION.GET_SUPERVISOR, {
        parentId: managerIds.map(m => m.value),
      });
      const supervisors = res.data?.data || [];

      const supervisorOptions = supervisors.map((user) => ({
        value: user._id,
        label: user.name,
      }));

      setDropdown((prev) => ({
        ...prev,
        supervisors: supervisors,
        supervisorOptions,
      }));
    } catch (error) {
      console.error("Error fetching supervisors:", error);
    }
  };

  const exportExcel = async () => {
    try {
      setVisible((prev) => ({ ...prev, loader: true }));
      
      // Build params for export the same way as getData
      const params = {};
      
      if (managerFilter.length > 0) {
        params.managerId = managerFilter.map(m => m.value);
      }
      
      if (supervisorFilter.length > 0) {
        params.supervisorId = supervisorFilter.map(s => s.value);
      }
      
      if (searchFilter) {
        params.search = searchFilter;
      }
      
      if (stateFilter) {
        params.states = [stateFilter];
      }
      
      if (cityFilter) {
        params.cities = [cityFilter];
      }
      
      const { status, data } = await Axios.get(API.REPORT.AGENT, params);
      if (status) window.open(data, "_blank");
    } catch (err) {
      console.log("ERROR==>", err);
    } finally {
      setVisible((prev) => ({ ...prev, loader: false }));
    }
  };

  const [states, setStatesAndUTs] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    setStatesAndUTs(State.getStatesOfCountry("IN"));
  }, []);

  const handleStateChange = (selectedOption) => {
    debugger
    if (!selectedOption) {
      setStateFilter(null);
      setCities([]);
      setCityFilter(null);
      return;
    }

    const stateCode = selectedOption.label;
    setStateFilter(stateCode);
    
    const stateCities = City.getCitiesOfState("IN", selectedOption.value);
    debugger
    setCities(stateCities);
    setCityFilter(null); // Reset city filter when state changes
  };

  const handleCityChange = (selectedOption) => {
    if (!selectedOption) {
      setCityFilter(null);
      return;
    }
    
    setCityFilter(selectedOption.value);
  };

  const handleSearchChange = (e) => {
    setSearchFilter(e.target.value);
  };

  const handleManagerChange = (selectedOptions) => {
    setManagerFilter(selectedOptions || []);
    loadSupervisorData(selectedOptions);
  };

  const handleSupervisorChange = (selectedOptions) => {
    setSupervisorFilter(selectedOptions || []);
  };

  // Auto-refresh data when any filter changes
  useEffect(() => {
    // Reset to first page when filters change
    setFilter(prev => ({...prev, offset: 0}));
  }, [managerFilter, supervisorFilter, searchFilter, stateFilter, cityFilter]);

  // Get data when filter changes (with debounce for search)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      getData();
    }, searchFilter ? 500 : 0); // Debounce search

    return () => clearTimeout(timeoutId);
  }, [filter.offset, filter.limit, managerFilter, supervisorFilter, searchFilter, stateFilter, cityFilter]);

  useEffect(() => {
    getDropdowns();
  }, []);

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "42px",
      backgroundColor: "#2d3748",
      borderColor: state.isFocused ? "#63b3ed" : "#4a5568",
      boxShadow: state.isFocused ? "0 0 0 1px #63b3ed" : provided.boxShadow,
      color: "white",
      "&:hover": {
        borderColor: "#63b3ed"
      }
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "white",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#a0aec0",
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "#2d3748",
      zIndex: 9999
    }),
    menuList: (provided) => ({
      ...provided,
      backgroundColor: "#2d3748",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#4a5568"
        : state.isFocused
        ? "#374151"
        : "#2d3748",
      color: "white",
      "&:active": {
        backgroundColor: "#4a5568"
      }
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#4a5568",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "white",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "white",
      ":hover": {
        backgroundColor: "#e53e3e",
        color: "white",
      },
    }),
    input: (provided) => ({
      ...provided,
      color: "white",
    })
  };

  // Create options for states and cities
  const stateOptions = states.map(state => ({
    value: state.isoCode,
    label: state.name
  }));

  const cityOptions = cities.map(city => ({
    value: city.name,
    label: city.name
  }));

  return (
    <Fragment>
      {visible.loader && <Loader />}
      {visible.deleteConformation?.visible && (
        <DeleteConfirmation
          data={visible.deleteConformation?.data}
          setVisible={setVisible}
          setData={setData}
          filter={filter}
          GETAPI={API.AGENT.LISTING}
          API={API.AGENT.DELETE}
        />
      )}
      <div className="container-fluid py-3">
        <div className="dateselect_navigation d-flex justify-content-between align-items-center">
          <div className="d-flex main_bar">
            {/* Manager Select - Multi Select */}
            <div className="mx-2" style={{ minWidth: "200px" }}>
              <Select
                value={managerFilter}
                options={dropdown.managerOptions}
                onChange={handleManagerChange}
                placeholder="Select Manager"
                isClearable
                isMulti
                styles={customStyles}
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>

            {/* Supervisor Select - Multi Select */}
            <div className="mx-2" style={{ minWidth: "200px" }}>
              <Select
                value={supervisorFilter}
                options={dropdown.supervisorOptions}
                onChange={handleSupervisorChange}
                placeholder="Select Supervisor"
                isClearable
                isMulti
                styles={customStyles}
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>

            {/* Fixed Search Box */}
            <div className="d-flex position-relative bg-gradient-dark rounded mx-2" style={{ height: "42px" }}>
              <i
                className="fa fa-search position-absolute top-50 translate-middle-y ms-3 text-white"
                style={{ zIndex: 10 }}
                aria-hidden="true"
              ></i>
              <input
                value={searchFilter}
                onChange={handleSearchChange}
                type="search"
                className="text-start form-control bg-dark text-white border-0 ps-5 h-100"
                style={{ 
                  cursor: "unset", 
                  width: "200px",
                  position: "relative"
                }}
                name="gsearch"
                placeholder="Search..."
              />
            </div>
            
            {/* State Select - Single Select */}
            <div className="mx-2" style={{ minWidth: "200px" }}>
              <Select
                value={stateOptions.find(opt => opt.label === stateFilter) || null}
                options={stateOptions}
                onChange={handleStateChange}
                placeholder="Select State"
                isClearable
                styles={customStyles}
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>
            
            {/* City Select - Single Select */}
            <div className="mx-2" style={{ minWidth: "200px" }}>
              <Select
                value={cityOptions.find(opt => opt.value === cityFilter) || null}
                options={cityOptions}
                onChange={handleCityChange}
                placeholder="Select City"
                isClearable
                styles={customStyles}
                className="react-select-container"
                classNamePrefix="react-select"
                isDisabled={!stateFilter}
              />
            </div>
          </div>
          <div className="d-flex align-items-center mob-bb">
            <div className="me-2 mob-margin-none">
              {(all || add) && (
                <Link to={EndPoints.ADD_AGENT}>
                  <button className="btn bg-gradient-dark float-end d-flex btn-md mb-1 align-items-center h-22">
                    <i className="fa fa-plus me-2 mt-1" aria-hidden="true"></i>
                    Add
                  </button>
                </Link>
              )}
            </div>
            <button
              className="btn bg-gradient-dark float-end d-flex btn-md mb-1 align-items-center h-22 data-del"
              onClick={exportExcel}
            >
              <i className="fa fa-download me-2 mt-1" aria-hidden="true"></i>
              Export
            </button>
          </div>
        </div>

        {data?.data?.length === 0 ? (
          <DataNotFound />
        ) : (
          <Fragment>
            <div className="overflow-table">
              <table className="page-table">
                <thead>
                  <tr>
                    <th scope="col" className="text-uppercase">
                      #
                    </th>
                    <th scope="col" className="text-uppercase">
                      Profile
                    </th>
                    <th scope="col" className="text-uppercase">
                      User Id
                    </th>
                    <th scope="col" className="text-uppercase">
                      Name
                    </th>
                    <th scope="col" className="text-uppercase">
                      Supervisor
                    </th>
                    {(all || edit || del) && (
                      <th scope="col" className="text-uppercase">
                        Action
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {data?.data?.map((val, i) => (
                    <tr key={val._id}>
                      <td>{++i + filter.offset}</td>
                      <td>
                        {val.image ? (
                          <img
                            className="profile"
                            src={val.image}
                            alt={val.name}
                          />
                        ) : (
                          NA
                        )}
                      </td>
                      <td>
                        <Link to={EndPoints.AGENT} state={val._id}>
                          {val.userName}
                        </Link>
                      </td>
                      <td>{val.name}</td>
                      <td>{val.supervisor?.name || NA}</td>
                      {(all || edit || del) && (
                        <td>
                          {(all || del) && (
                            <div
                              className="position-relative"
                              ref={(el) => (dropdownRefs.current[val._id] = el)}
                            >
                              <i
                                className="material-icons ps-2 cursior-pointer"
                                onClick={(e) => handleAction(val._id, e)}
                              >
                                more_horiz
                              </i>
                              <ul
                                className="icon-list"
                                style={{
                                  display: val?.action ? "block" : "none",
                                }}
                              >
                                <li onClick={() => handleDelete(val)}>
                                  Delete Data
                                </li>
                                <li onClick={() => handleBlockUnblock(val)}>
                                  {val.isBlocked
                                    ? BLOCK_STATUS.UN_BLOCKED
                                    : BLOCK_STATUS.BLOCKED}
                                </li>
                                <li>
                                  <Link
                                    style={{ zIndex: "99" }}
                                    to={EndPoints.EDIT_AGENT}
                                    state={{
                                      ...val,
                                      password: val.dcryptedPass,
                                      parentId: val.supervisor?._id,
                                    }}
                                  >
                                    Edit
                                  </Link>
                                </li>
                              </ul>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Paginate
              count={data?.count}
              filter={filter}
              setFilter={setFilter}
              sliceKey={"agent"}
              forcePage={paginate?.agent / 10}
            />
          </Fragment>
        )}
      </div>
    </Fragment>
  );
};

export default Agents;